import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, Placeholder } from 'react-bootstrap';
import { AlertCircle, ArrowLeft, CheckCircle2, Satellite } from 'lucide-react';

import Wrapper from '../../../components/Wrapper';
import { findFarmRow } from './satelliteApi';
import { farmFromRow } from './satelliteModel';
import { renderSatText, satDate, t, toneColour } from './satelliteText';
import useFarmSatelliteInsights from './useFarmSatelliteInsights';
import SatelliteFoundSection from './SatelliteFoundSection';
import SatelliteMeasuresSection from './SatelliteMeasuresSection';
import SeasonSection from './SeasonSection';
import CropProgressSection from './CropProgressSection';

/** The field's verdict, worded as the farmer app's banner words it (VerdictBanner). */
function VerdictBanner({ verdict, loading }) {
  if (loading || !verdict) {
    return (
      <div className="border rounded p-3 mb-4">
        <Placeholder as="div" animation="glow">
          <Placeholder xs={5} />
        </Placeholder>
      </div>
    );
  }
  const unknown = verdict.tone === 'unknown';
  const colour = toneColour(unknown ? 'neutral' : verdict.tone);
  const Icon = verdict.tone === 'good' ? CheckCircle2 : unknown ? Satellite : AlertCircle;
  const sub = unknown ? t('verdictSubUnknown') : renderSatText(verdict.todos && verdict.todos[0]);
  return (
    <div className="rounded p-3 mb-4 d-flex gap-3 align-items-start" style={{ border: `1px solid ${colour}`, borderLeftWidth: 6 }} role="status">
      <Icon size={22} color={colour} className="flex-shrink-0 mt-1" />
      <div>
        <div className="fw-bold" style={{ color: colour }}>
          {t(verdict.titleKey, { n: verdict.count })}
        </div>
        {sub && <small className="text-muted">{sub}</small>}
      </div>
    </div>
  );
}

const isSettled = (entry) => Boolean(entry) && entry.status !== 'loading';

/**
 * Satellite Insights for Support staff: one farm's satellite picture, read
 * exactly as the farmer app reads it — what the satellite found, what it
 * measures, and season by season. Read-only.
 *
 * Opened from the Farms table, which hands over the farm's row; a direct link
 * looks the farm up by id. Nothing about the satellite is asked until this
 * page is open, and never for a farm without a usable boundary.
 */
export default function FarmSatellitePage() {
  const { farmId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const handed = location.state && location.state.farm;
  const handedRow = handed && String(handed.farm_id) === String(farmId) ? handed : null;

  const [row, setRow] = useState(handedRow);
  const [status, setStatus] = useState(handedRow ? 'ready' : 'loading');

  useEffect(() => {
    if (handedRow) {
      setRow(handedRow);
      setStatus('ready');
      return undefined;
    }
    let live = true;
    setStatus('loading');
    findFarmRow(farmId)
      .then((found) => {
        if (!live) return;
        setRow(found);
        setStatus(found ? 'ready' : 'notFound');
      })
      .catch(() => {
        if (live) setStatus('error');
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farmId]);

  const farm = useMemo(() => farmFromRow(row), [row]);
  const insights = useFarmSatelliteInsights(status === 'ready' ? farm : null);
  const { entries, signals } = insights;

  /** Only what has arrived: a layer still loading is neither a finding nor "no reading". */
  const settledSignals = useMemo(
    () => Object.fromEntries(Object.entries(signals).filter(([id, s]) => s && isSettled(entries[id]))),
    [signals, entries]
  );

  const back = (
    <Button variant="link" className="px-0 mb-2 d-inline-flex align-items-center gap-1" onClick={() => navigate('/farms')}>
      <ArrowLeft size={16} /> Farms
    </Button>
  );

  let content;
  if (status === 'loading') {
    content = <h4 className="text-muted py-5 text-center">Loading farm...</h4>;
  } else if (status === 'notFound' || status === 'error' || !farm) {
    content = (
      <div className="alert alert-warning">
        {status === 'error' ? 'Could not load this farm. Please try again.' : 'Farm not found.'}
      </div>
    );
  } else if (!farm.hasBoundary) {
    content = (
      <div className="alert alert-info">
        {farm.name} has no usable boundary, so there is no satellite picture to show.
      </div>
    );
  } else {
    content = (
      <>
        <VerdictBanner verdict={insights.verdict} loading={insights.verdictLoading} />

        <section className="mb-5">
          <div className="d-flex flex-wrap justify-content-between align-items-baseline mb-2">
            <h2 className="h4 fw-bold mb-0">{t('mfSatFound')}</h2>
            {insights.pictureDate && (
              <small className="text-muted">{t('satPictureDate', { date: satDate(insights.pictureDate) })}</small>
            )}
          </div>
          <SatelliteFoundSection
            farm={farm}
            entries={entries}
            signals={signals}
            settledSignals={settledSignals}
            patches={insights.patches}
            pixels={insights.pixels}
            failed={insights.anyFailed}
            onRetry={insights.reload}
          />
        </section>

        <section className="mb-5">
          <h2 className="h4 fw-bold mb-2">{t('mfWatchTitleAll')}</h2>
          <SatelliteMeasuresSection entries={entries} signals={settledSignals} />
        </section>

        <section className="mb-4">
          <div className="d-flex flex-wrap justify-content-between align-items-baseline mb-2">
            <h2 className="h4 fw-bold mb-0">{t('seasonTitle')}</h2>
            <small className="text-muted">{t('seasonScoreLabel')}</small>
          </div>
          <SeasonSection seasons={insights.seasons} onRetry={insights.reload} />
        </section>
      </>
    );
  }

  return (
    <Wrapper>
      <div className="reports-container py-4">
        {back}
        {farm && status === 'ready' && (
          <div className="mb-3">
            <h1 className="fw-bolder mb-1">{farm.name}</h1>
            <div className="text-muted">
              {[`${farm.acres.toFixed(2)} ac`, farm.ownerName ? `Owner: ${farm.ownerName}` : null, farm.id]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </div>
        )}
        {farm && status === 'ready' && farm.id != null && <CropProgressSection farm={farm} />}
        {content}
      </div>
    </Wrapper>
  );
}
